"use client";
import {JSX, useEffect, useMemo, useState} from "react";

import SmartImage from "@/shared/ui/smart-image";

import {useSnapshot} from "valtio";

import {type LocationOption, LocationSearchView} from "@/features/locations";
import {GuestsButtonSm, GuestsView} from "@/features/rental/guests";
import {type Payload, SimpleSearch, type SimpleSearchInstance} from "@/features/rental/simple-search/simple-search.model";

import {useServerRouter} from "@/shared/hooks/navigation/use-server-router";
import {useModel} from "@/shared/hooks/state-management/use-model";
import {useTranslation} from "@/shared/i18n";
import {DateHelper} from "@/shared/lib/helpers/date";
import {ButtonBase, ButtonWithImage} from "@/shared/ui/buttons";
import {DateRangePickerField} from "@/shared/ui/form/datepickers";
import {DateRangeFilterTriggerSmall} from "@/shared/ui/form/datepickers/range/parts";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";

import "./styles/simple-search-view.scss";

export interface SimpleSearchSubmitParams {
    startDate: Date | null;
    endDate: Date | null;
    adults: number;
    children: number;
    pets: number;
}

interface SimpleSearchViewProps {
    className?: string;
    initialLocation?: LocationOption;
    showLocation?: boolean;
    onSubmit?: (params: SimpleSearchSubmitParams) => void;
    submitIcon?: string;
    /** "pill": each field in its own rounded box with an icon, and a labeled submit button (franchise hero v2) */
    variant?: SimpleSearchVariant;
    /** Submit button label, used by the "pill" variant */
    submitLabel?: string;
}

export type SimpleSearchVariant = "default" | "pill";

/**
 * Format guests count as "3 Guest" or "3 Guests"
 */
function formatGuestsCompact(totalGuests: number): string {
    return `${totalGuests} Guest${totalGuests !== 1 ? "s" : ""}`;
}

export function SimpleSearchView({
    className,
    initialLocation,
    showLocation = true,
    onSubmit,
    submitIcon = "search",
    variant = "default",
    submitLabel,
}: SimpleSearchViewProps): JSX.Element {
    const isPill = variant === "pill";
    const [search] = useModel<SimpleSearchInstance, [Payload?]>(SimpleSearch, initialLocation ? {location: initialLocation} : undefined);
    const snapshot = useSnapshot(search);
    const [isExpanded, setIsExpanded] = useState(!showLocation);
    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const {t} = useTranslation("search");
    useServerRouter(search);

    // Track which fields user has confirmed (selected value and closed dropdown)
    const [confirmedFields, setConfirmedFields] = useState<{
        location: boolean;
        dates: boolean;
        guests: boolean;
    }>({location: false, dates: false, guests: false});

    // Track which field is currently being edited
    const [editingField, setEditingField] = useState<"location" | "dates" | "guests" | null>(null);

    // Formatted display values
    const locationDisplay = useMemo(() => {
        if (snapshot.locationSearch.selectedOption) {
            return snapshot.locationSearch.selectedOption.name;
        }
        return snapshot.locationSearch.value || "";
    }, [snapshot.locationSearch.selectedOption, snapshot.locationSearch.value]);

    const dateDisplay = useMemo(() => {
        return DateHelper.formatDateRangeCompact(snapshot.dateRange.startDate, snapshot.dateRange.endDate);
    }, [snapshot.dateRange.startDate, snapshot.dateRange.endDate]);

    const guestsDisplay = useMemo(() => {
        const guestsPart = formatGuestsCompact(snapshot.guests.totalGuests);
        if (snapshot.guests.totalPets > 0) {
            return `${guestsPart}, ${t("dogsCount", {count: snapshot.guests.totalPets})}`;
        }
        return guestsPart;
    }, [snapshot.guests.totalGuests, snapshot.guests.totalPets, t]);

    // Listen to events from models to confirm selection or clear
    useEffect(() => {
        // When location is selected, mark as confirmed and stop editing
        const unsubLocationSelect = search.locationSearch.events.on("select", () => {
            setConfirmedFields((prev) => ({...prev, location: true}));
            setEditingField(null);
        });

        // When location is cleared, reset confirmed state
        const unsubLocationClear = search.locationSearch.events.on("clear", () => {
            setConfirmedFields((prev) => ({...prev, location: false}));
        });

        // When guests apply is called, mark as confirmed and stop editing
        const unsubGuests = search.guests.events.on("apply", () => {
            setConfirmedFields((prev) => ({...prev, guests: true}));
            setEditingField(null);
        });

        // When dates are cleared, reset confirmed state
        const unsubDatesClear = search.dateRange.events.on("clear", () => {
            setConfirmedFields((prev) => ({...prev, dates: false}));
        });

        return () => {
            unsubLocationSelect();
            unsubLocationClear();
            unsubGuests();
            unsubDatesClear();
        };
    }, [search.locationSearch.events, search.guests.events, search.dateRange.events]);

    const handleLocationClick = (): void => {
        setIsExpanded(true);
        setEditingField("location");
    };

    const handleDatePickerClick = (): void => {
        setIsDatePickerOpen(true);
        setEditingField("dates");
    };

    const handleGuestsClick = (): void => {
        setEditingField("guests");
        // Defer so GuestsView mounts and subscribes to "open" before we emit
        setTimeout(() => search.guests.openDropDown(), 0);
    };

    const handleSubmit = (): void => {
        if (onSubmit) {
            const {adults, children, pets} = search.guests.value;
            onSubmit({
                startDate: search.dateRange.startDate,
                endDate: search.dateRange.endDate,
                adults,
                children,
                pets,
            });
            return;
        }
        search.submit();
    };

    // Check if fields have actual values
    const hasLocationValue = !!snapshot.locationSearch.selectedOption;
    const hasDateValue = snapshot.dateRange.hasValue;
    const hasGuestsValue = snapshot.guests.totalGuests > 0;

    // Determine collapsed state: field is collapsed if confirmed AND has value AND is NOT being edited
    // Each field collapses/expands independently
    const isLocationCollapsed = confirmedFields.location && hasLocationValue && editingField !== "location";
    const isDatesCollapsed = confirmedFields.dates && hasDateValue && editingField !== "dates";
    const isGuestsCollapsed = confirmedFields.guests && hasGuestsValue && editingField !== "guests";

    // Overall collapsed state (for styling)
    const hasAnyCollapsed = isLocationCollapsed || isDatesCollapsed || isGuestsCollapsed;

    const renderFieldIcon = (icon: string): JSX.Element | null =>
        isPill ? (
            <span className="simple-search__field-icon">
                <SmartImage src={getStaticPathIcon(icon)} alt="" width={20} height={20} />
            </span>
        ) : null;

    // The "pill" variant keeps the field label above the collapsed value
    const renderCollapsedValue = (label: string | undefined, value: string | undefined): JSX.Element =>
        isPill ? (
            <div className="simple-search__collapsed">
                <span className="simple-search__collapsed-label">{label}</span>
                <div className="simple-search__collapsed-value">{value}</div>
            </div>
        ) : (
            <div className="simple-search__collapsed-value">{value}</div>
        );

    return (
        <div
            className={`simple-search ${isPill ? "simple-search--pill" : ""} ${className || ""} ${isExpanded ? "simple-search--expanded" : ""} ${hasAnyCollapsed ? "simple-search--has-collapsed" : ""}`}
        >
            <div className="simple-search__fields">
                {showLocation && (
                    <div
                        className={`simple-search__field simple-search__field-destination ${hasLocationValue && editingField !== "location" ? "simple-search__field--collapsed" : ""}`}
                        onClick={handleLocationClick}
                    >
                        {renderFieldIcon("location")}
                        {editingField === "location" || !hasLocationValue ? (
                            <LocationSearchView
                                model={search.locationSearch}
                                dropdownOffset={13}
                                label={t("location")}
                                placeholder={t("locationPlaceholder")}
                                className="simple-search__location"
                                hidePlaceholderOnFocus={true}
                                onClose={() => {
                                    setEditingField(null);
                                    setIsExpanded(false);
                                }}
                            />
                        ) : (
                            renderCollapsedValue(t("location"), locationDisplay)
                        )}
                    </div>
                )}

                <div
                    className={`simple-search__field simple-search__field-dates ${isDatesCollapsed ? "simple-search__field--collapsed" : ""}`}
                    onClick={handleDatePickerClick}
                >
                    {renderFieldIcon("calendar")}
                    {isDatesCollapsed ? (
                        renderCollapsedValue(t("checkInOut"), dateDisplay)
                    ) : (
                        <DateRangePickerField
                            model={search.dateRange}
                            label={t("checkInOut")}
                            startPlaceholder={t("checkIn")}
                            endPlaceholder={t("checkOut")}
                            showClearButton={false}
                            singleCalendar={true}
                            dropdownOffset={17}
                            renderTrigger={(props) => <DateRangeFilterTriggerSmall {...props} />}
                            className="simple-search__date-picker"
                            controlledIsOpen={isDatePickerOpen}
                            onOpenChange={(open) => {
                                setIsDatePickerOpen(open);
                                // When closing
                                if (!open) {
                                    // If has dates selected, mark as confirmed
                                    if (search.dateRange.hasValue) {
                                        setConfirmedFields((prev) => ({...prev, dates: true}));
                                    }
                                    // Only reset editing field if dates was being edited (not if user clicked another field)
                                    setEditingField((current) => (current === "dates" ? null : current));
                                }
                            }}
                        />
                    )}
                </div>

                <div
                    className={`simple-search__field simple-search__field-guests ${isGuestsCollapsed ? "simple-search__field--collapsed" : ""}`}
                    onClick={handleGuestsClick}
                >
                    {renderFieldIcon("person")}
                    {isGuestsCollapsed ? (
                        renderCollapsedValue(t("guests"), guestsDisplay)
                    ) : (
                        <GuestsView
                            dropdownOffset={17}
                            model={search.guests}
                            label={t("guests")}
                            className="simple-search__guests-inner"
                            TriggerButton={GuestsButtonSm}
                        />
                    )}
                </div>
            </div>
            {isPill ? (
                <ButtonBase variant="primary" size="md" width="auto" weight="semibold" onClick={handleSubmit} className="simple-search__submit">
                    <span className="simple-search__submit-icon">
                        <SmartImage src={getStaticPathIcon(submitIcon)} alt="" width={18} height={18} />
                    </span>
                    {submitLabel || t("searchButton")}
                </ButtonBase>
            ) : (
                <ButtonWithImage width={32} height={32} onClick={handleSubmit} variant="primary" icon={getStaticPathIcon(submitIcon)} />
            )}
        </div>
    );
}
